"use client";

import { usePathname } from "next/navigation";
import type { ToasterProps } from "sonner";
import { Toaster as Sonner, toast as sonnerToast } from "sonner";

const BACKEND_PATH_PREFIXES = [
  "/dashboard",
  "/intranet",
  "/login",
  "/request-password",
  "/reset-password",
  "/registrieren",
] as const;

const isBackendPath = (pathname: string | null) =>
  pathname != null &&
  BACKEND_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

const getToastClassNames = (isBackend: boolean) => ({
  error: "!text-black !bg-white !border-gray-200 [&_[data-icon]]:!text-red-600",
  success: isBackend
    ? "!text-black !bg-white !border-gray-200 !rounded-2xl [&_[data-icon]]:!text-[var(--c-accent)]"
    : "!text-black !bg-white !border-gray-200 [&_[data-icon]]:!text-green-600",
  warning:
    "!text-black !bg-white !border-gray-200 [&_[data-icon]]:!text-yellow-600",
  info: "!text-black !bg-white !border-gray-200 [&_[data-icon]]:!text-blue-600",
  default: "!text-black !bg-white !border-gray-200",
});

const Toaster = ({ ...props }: ToasterProps) => {
  const pathname = usePathname();
  const isBackend = isBackendPath(pathname);

  return (
    <Sonner
      theme="light"
      className="toaster group"
      position="bottom-right"
      expand={false}
      closeButton
      richColors={false}
      toastOptions={{
        style: {
          backgroundColor: "white",
          border: "1px solid #e5e7eb",
          ...(isBackend ? { borderRadius: "16px" } : {}),
        },
        classNames: getToastClassNames(isBackend),
        unstyled: false,
      }}
      {...props}
    />
  );
};

// Custom toast function that supports HTML content
const toast = {
  ...sonnerToast,
  error: (title: string, options?: { description?: string }) => {
    if (options?.description) {
      return sonnerToast.error(title, {
        ...options,
        description: (
          <div dangerouslySetInnerHTML={{ __html: options.description }} />
        ),
      });
    }
    return sonnerToast.error(title, options);
  },
  success: (title: string, options?: { description?: string }) => {
    if (options?.description) {
      return sonnerToast.success(title, {
        ...options,
        description: (
          <div dangerouslySetInnerHTML={{ __html: options.description }} />
        ),
      });
    }
    return sonnerToast.success(title, options);
  },
  info: (title: string, options?: { description?: string }) => {
    if (options?.description) {
      return sonnerToast.info(title, {
        ...options,
        description: (
          <div dangerouslySetInnerHTML={{ __html: options.description }} />
        ),
      });
    }
    return sonnerToast.info(title, options);
  },
  warning: (title: string, options?: { description?: string }) => {
    if (options?.description) {
      return sonnerToast.warning(title, {
        ...options,
        description: (
          <div dangerouslySetInnerHTML={{ __html: options.description }} />
        ),
      });
    }
    return sonnerToast.warning(title, options);
  },
};

export { Toaster, toast };
